import Input from '@superfunnel/ui-core/v2/Input';
import Label from '@superfunnel/ui-core/v2/Label';

/**
 * Represents a limit value that can be either a number or unlimited (null)
 */
export interface LimitValue {
  value: string;
  isUnlimited: boolean;
}

/**
 * All limit fields managed by the form
 */
export interface PlanLimitsFormValues {
  domains: LimitValue;
  pages: LimitValue;
  publishedPages: LimitValue;
  members: LimitValue;
  aiCredits: LimitValue;
  buffer: string;
}

/**
 * Parse a number/null value into LimitValue format
 */
export function parseLimitValue(value: number | null | undefined): LimitValue {
  if (value === null || value === undefined) {
    return { value: '', isUnlimited: true };
  }
  return { value: String(value), isUnlimited: false };
}

/**
 * Convert LimitValue back to number/null for API calls
 */
export function toLimitNumber(limitValue: LimitValue): number | null {
  if (limitValue.isUnlimited) {
    return null;
  }
  const num = parseInt(limitValue.value, 10);
  return isNaN(num) ? null : num;
}

/**
 * Get default empty form values
 */
export function getDefaultLimitValues(): PlanLimitsFormValues {
  return {
    domains: { value: '', isUnlimited: false },
    pages: { value: '', isUnlimited: true },
    publishedPages: { value: '', isUnlimited: false },
    members: { value: '', isUnlimited: false },
    aiCredits: { value: '', isUnlimited: false },
    buffer: '0',
  };
}

/**
 * Convert form values to the customLimits object for API calls
 */
export function formValuesToCustomLimits(values: PlanLimitsFormValues) {
  return {
    domains: toLimitNumber(values.domains),
    pages: toLimitNumber(values.pages),
    publishedPages: toLimitNumber(values.publishedPages),
    members: toLimitNumber(values.members),
    aiCredits: toLimitNumber(values.aiCredits),
    buffer: parseFloat(values.buffer) || 0,
  };
}

interface PlanLimitsFormProps {
  values: PlanLimitsFormValues;
  onChange: (values: PlanLimitsFormValues) => void;
  disabled?: boolean;
  showBuffer?: boolean;
}

/**
 * Reusable form component for editing plan limits.
 * Used by both CreateCustomPlanModal and EditSubscriptionLimitsModal.
 */
export function PlanLimitsForm({
  values,
  onChange,
  disabled = false,
  showBuffer = true,
}: PlanLimitsFormProps) {
  const updateField = (
    field: keyof Omit<PlanLimitsFormValues, 'buffer'>,
    value: LimitValue,
  ) => {
    onChange({ ...values, [field]: value });
  };

  return (
    <div className="grid grid-cols-2 gap-5">
      <LimitInput
        label="Domains"
        value={values.domains}
        onChange={(value) => updateField('domains', value)}
        disabled={disabled}
        placeholder="e.g., 5"
      />

      <LimitInput
        label="Pages (total)"
        value={values.pages}
        onChange={(value) => updateField('pages', value)}
        disabled={disabled}
        placeholder="e.g., 10"
      />

      <LimitInput
        label="Published Pages"
        value={values.publishedPages}
        onChange={(value) => updateField('publishedPages', value)}
        disabled={disabled}
        placeholder="e.g., 5"
      />

      <LimitInput
        label="Team Members"
        value={values.members}
        onChange={(value) => updateField('members', value)}
        disabled={disabled}
        placeholder="e.g., 5"
      />

      <LimitInput
        label="AI Credits (dollars)"
        value={values.aiCredits}
        onChange={(value) => updateField('aiCredits', value)}
        disabled={disabled}
        placeholder="e.g., 50"
      />

      {showBuffer && (
        <div className="space-y-1">
          <Label className="text-sm font-medium">Buffer (0-1)</Label>
          <Input
            type="number"
            step="0.1"
            min="0"
            max="1"
            value={values.buffer}
            onChange={(e) => onChange({ ...values, buffer: e.target.value })}
            placeholder="0"
            disabled={disabled}
          />
          <p className="text-xs text-gray-400">
            Grace percentage above limits (e.g., 0.1 = 10% buffer)
          </p>
        </div>
      )}
    </div>
  );
}

interface LimitInputProps {
  label: string;
  value: LimitValue;
  onChange: (value: LimitValue) => void;
  disabled?: boolean;
  placeholder?: string;
}

function LimitInput({
  label,
  value,
  onChange,
  disabled,
  placeholder,
}: LimitInputProps) {
  return (
    <div className="space-y-1">
      <Label className="text-sm font-medium">{label}</Label>
      <div className="flex items-center gap-3">
        <Input
          type="number"
          value={value.isUnlimited ? '' : value.value}
          onChange={(e) =>
            onChange({ value: e.target.value, isUnlimited: false })
          }
          placeholder={value.isUnlimited ? 'Unlimited' : placeholder}
          disabled={disabled || value.isUnlimited}
          className="flex-1"
        />
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={value.isUnlimited}
            onChange={(e) =>
              onChange({ ...value, isUnlimited: e.target.checked })
            }
            disabled={disabled}
            className="h-4 w-4 rounded border-gray-300"
          />
          Unlimited
        </label>
      </div>
    </div>
  );
}
